import { Head, Link } from "@inertiajs/react";
import { BlogLayout } from "@/components/layout";
import { Card } from "@/components/ui/card";
import { blogPosts, type BlogPost } from "@/data/blogPosts";
import {
  ArrowRight,
  Clock,
  Flame,
  Heart,
  Sparkles,
  Users,
} from "lucide-react";
import { useState } from "react";

type Category = BlogPost["category"] | "todos";

const categories: { value: Category; label: string }[] = [
  { value: "todos", label: "Todos" },
  { value: "tinder", label: "Tinder" },
  { value: "bumble", label: "Bumble" },
  { value: "badoo", label: "Badoo" },
  { value: "inner-circle", label: "Inner Circle" },
  { value: "geral", label: "Geral" },
];

const categoryIcons: Record<BlogPost["category"], React.ReactNode> = {
  tinder: <Flame size={14} />,
  bumble: <Heart size={14} />,
  badoo: <Users size={14} />,
  "inner-circle": <Sparkles size={14} />,
  geral: <ArrowRight size={14} />,
};

const categoryColors: Record<BlogPost["category"], string> = {
  tinder: "bg-red-500/10 text-red-400 border-red-500/20",
  bumble: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  badoo: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  "inner-circle": "bg-blue-500/10 text-blue-400 border-blue-500/20",
  geral: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
};

function formatDate(dateStr: string) {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function Index() {
  const [activeCategory, setActiveCategory] = useState<Category>("todos");

  const filtered =
    activeCategory === "todos"
      ? blogPosts
      : blogPosts.filter((p) => p.category === activeCategory);

  const featured = blogPosts[0];
  const rest = filtered.filter((p) => p.slug !== featured?.slug);

  return (
    <BlogLayout title="Blog | fotoCerta">
      <Head>
        <meta
          name="description"
          content="Dicas práticas de fotos pra apps de namoro como Tinder, Bumble e Badoo. Aprenda a escolher a melhor foto de perfil e conseguir mais matches. Blog fotoCerta."
        />
        <meta
          property="og:title"
          content="Dicas de Fotos pra Tinder, Bumble e Apps de Namoro | Blog fotoCerta"
        />
        <meta
          property="og:description"
          content="Dicas práticas de fotos pra apps de namoro como Tinder, Bumble e Badoo. Aprenda a escolher a melhor foto de perfil e conseguir mais matches."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fotocerta.com/blog" />
        <meta property="og:image" content="https://fotocerta.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dicas de Fotos pra Tinder, Bumble e Apps de Namoro | Blog fotoCerta" />
        <meta name="twitter:description" content="Dicas práticas de fotos pra apps de namoro como Tinder, Bumble e Badoo. Aprenda a escolher a melhor foto de perfil e conseguir mais matches." />
        <meta name="twitter:image" content="https://fotocerta.com/og-image.png" />
        <link rel="canonical" href="https://fotocerta.com/blog" />
      </Head>

      <div className="mx-auto max-w-5xl px-4 py-10">
        <header className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
            Dicas de Fotos pra Apps de Namoro
          </h1>
          <p className="mt-2 text-lg text-muted-foreground">
            Como escolher a melhor foto pro Tinder, Bumble, Badoo e outros apps.
            Dicas pra melhorar seu perfil e conseguir mais matches.
          </p>
        </header>

        {/* Featured post */}
        {featured && activeCategory === "todos" && (
          <Link
            href={`/blog/${featured.slug}`}
            className="group mb-10 block"
          >
            <Card className="overflow-hidden border-border bg-card transition-colors hover:border-primary/30">
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-2 text-sm">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${categoryColors[featured.category]}`}
                  >
                    {categoryIcons[featured.category]}
                    {
                      categories.find((c) => c.value === featured.category)
                        ?.label
                    }
                  </span>
                  <span className="text-muted-foreground">
                    {formatDate(featured.publishedAt)}
                  </span>
                </div>
                <h2 className="mt-3 text-2xl font-bold tracking-tight transition-colors group-hover:text-primary md:text-3xl">
                  {featured.title}
                </h2>
                <p className="mt-2 text-muted-foreground">
                  {featured.excerpt}
                </p>
                <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {featured.readingTime} min de leitura
                  </span>
                  <span className="inline-flex items-center gap-1 font-medium text-primary transition-colors group-hover:text-primary/80">
                    Ler artigo <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Card>
          </Link>
        )}

        {/* Category filters */}
        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                activeCategory === cat.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Post grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(activeCategory === "todos" ? rest : filtered).map((post) => (
            <PostCard key={post.slug} post={post} />
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="py-20 text-center text-muted-foreground">
            Nenhum artigo nessa categoria ainda.
          </p>
        )}
      </div>
    </BlogLayout>
  );
}

function PostCard({ post }: { post: BlogPost }) {
  return (
    <Link href={`/blog/${post.slug}`} className="group">
      <Card className="flex h-full flex-col border-border bg-card p-5 transition-colors hover:border-primary/30">
        <div className="flex items-center gap-2 text-xs">
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${categoryColors[post.category]}`}
          >
            {categoryIcons[post.category]}
            {categories.find((c) => c.value === post.category)?.label}
          </span>
        </div>
        <h3 className="mt-3 text-base font-semibold leading-snug tracking-tight transition-colors group-hover:text-primary">
          {post.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-muted-foreground">
          {post.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock size={12} />
            {post.readingTime} min
          </span>
          <span>{formatDate(post.publishedAt)}</span>
        </div>
      </Card>
    </Link>
  );
}
